import { Radio, Stack, Text } from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import React from 'react'
import {
  DefinedFulfilmentType,
  FulfilmentType,
} from '../../../../../../models/fulfilment'
import { LineItemFulfilmentNote } from '../../LineItemFulfilmentNote'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'
import { FulfilmentSelect } from '../../../../../react/modals/mark-as-unfulfilled-modal/Steps/Step1/FulfillmentSelect'

const TContext = 'Line item fulfilment popover'
const TestId = 'lif-details-view'

type LineItemFulfilmentDetailsViewProps = {
  productName: string
  fulfilmentType?: DefinedFulfilmentType
  fulfilmentNote?: string
  onSelect: (opt: DefinedFulfilmentType) => void
  onNoteChange: (note: string) => void
  showSpecialOrderMessage?: boolean
}

export const LineItemFulfilmentDetailsViewLegacy: React.FC<LineItemFulfilmentDetailsViewProps> = ({
  productName,
  fulfilmentType = FulfilmentType.DELIVERY,
  fulfilmentNote = '',
  onSelect,
  onNoteChange,
  showSpecialOrderMessage = true,
}: LineItemFulfilmentDetailsViewProps) => {
  const t = useT()
  const ffSpecialOrder = getFeatureFromCache('inventory_special_orders')
  const onTypeChanged = (type: FulfilmentType) => {
    if (type === FulfilmentType.UNSPECIFIED) {
      return
    }
    onSelect(type)
  }
  return (
    <Stack space={6} data-testid={TestId}>
      <Text type="label">
        <h3>
          {t('Choose how “{productName}” will be fulfilled', {
            _context: TContext,
            productName,
          })}
        </h3>
      </Text>
      {ffSpecialOrder ? (
        <FulfilmentSelect
          showUnspecified={false}
          defaultType={fulfilmentType}
          onTypeChange={onTypeChanged}
          stretched={true}
          message={t(
            'You can select one method per sale. To set another method, create another sale.',
            {
              _context: 'Note explaining how fulfillment methods work',
            }
          )}
          outOfStockMessage={
            showSpecialOrderMessage
              ? t(
                  'This product is out of stock. If incoming stock is insufficient, it will be available for special order and ready to be fulfilled upon restock.',
                  {
                    _context: 'Special order product info message',
                  }
                )
              : undefined
          }
        />
      ) : (
        <>
          <Text>
            <T
              _str="Only one fulfillment method can be selected for the items that require fulfillment. To record another method, create another sale."
              _context={TContext}
            />
          </Text>
          <Text>
            {t(
              'Record the fulfillment details for “{productName}” in the fulfillment note.',
              {
                _context: TContext,
                productName,
              }
            )}
          </Text>
          <Stack space={2}>
            <Text type="label">
              <T _str="Fulfillment method" _context={TContext} />
            </Text>
            <Radio
              className="vd-pl0"
              name="fulfilment_method"
              label={t('Delivery', { _context: TContext })}
              checked={fulfilmentType === FulfilmentType.DELIVERY}
              value={FulfilmentType.DELIVERY}
              onChange={() => onSelect(FulfilmentType.DELIVERY)}
            />
            <Radio
              className="vd-pl0"
              name="fulfilment_method"
              label={t('Pickup', { _context: TContext })}
              checked={fulfilmentType === FulfilmentType.PICKUP}
              value={FulfilmentType.PICKUP}
              onChange={() => onSelect(FulfilmentType.PICKUP)}
            />
          </Stack>
        </>
      )}

      <LineItemFulfilmentNote note={fulfilmentNote} onChange={onNoteChange} />
    </Stack>
  )
}
